import { DragDropProvider } from "@dnd-kit/react";
import { useDisclosure } from "@mantine/hooks";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useParams } from "next/navigation";
import { useCallback, useMemo, useState } from "react";

import {
  formatBasicQuestionColumns,
  getColumnVisibilityState,
} from "./columns";
import { TableWrapper, type TableEmptyState } from "./table-wrapper";
import { SortableTableRow } from "@/components/sortable-table-row";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { useGetDragEndEventHandler } from "@/hooks/dnd-kit.hooks";
import {
  type ReviewPageBasicQuestion,
  QuestionType,
} from "@/types/screener-builder";
import { buildTestId } from "@/utils/helpers";

interface BasicQuestionTableProps {
  addQuestionTestId?: string;
  allowDeleteLastQuestion?: boolean;
  basicQuestions: ReviewPageBasicQuestion[];
  emptyState?: TableEmptyState;
  isReadOnly: boolean;
  isUnframed?: boolean;
  onBasicQuestionReorder: (
    updater: (
      questions: ReviewPageBasicQuestion[],
    ) => ReviewPageBasicQuestion[],
  ) => void;
  onDeleteQuestion: (questionId: string, closeWarningModal: () => void) => void;
  openQuestionModal: () => void;
  setSelectedQuestion: (question: ReviewPageBasicQuestion) => void;
}

export const BasicQuestionTable = ({
  addQuestionTestId,
  allowDeleteLastQuestion = false,
  basicQuestions,
  emptyState,
  isReadOnly,
  isUnframed = false,
  onBasicQuestionReorder,
  onDeleteQuestion,
  openQuestionModal,
  setSelectedQuestion,
}: BasicQuestionTableProps) => {
  const { id: screenerId } = useParams<{ id?: string }>() ?? {};
  const track = useTrackAnalytics({ screener_id: screenerId });
  const onDragEnd = useGetDragEndEventHandler(onBasicQuestionReorder);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [
    warningModalOpened,
    { open: openWarningModal, close: closeWarningModal },
  ] = useDisclosure(false);

  const nonDraggableQuestionTypes = useMemo(
    () => [QuestionType.Name, QuestionType.Email, QuestionType.Phone],
    [],
  );
  const isRowLocked = useCallback(
    (row: ReviewPageBasicQuestion) => {
      if (row.questionType === undefined) {
        return false;
      }

      const nonDefaultBasicQuestions = basicQuestions.filter(
        ({ questionType }) => !nonDraggableQuestionTypes.includes(questionType),
      );

      return (
        (!allowDeleteLastQuestion && nonDefaultBasicQuestions.length <= 1) ||
        nonDraggableQuestionTypes.includes(row.questionType)
      );
    },
    [allowDeleteLastQuestion, basicQuestions, nonDraggableQuestionTypes],
  );

  const handleEdit = useCallback(
    (row: ReviewPageBasicQuestion) => {
      setSelectedQuestion(row);
      openQuestionModal();
    },
    [setSelectedQuestion, openQuestionModal],
  );

  const confirmDelete = useCallback(() => {
    if (deleteId) {
      closeWarningModal();

      onDeleteQuestion(deleteId, () => {
        setDeleteId(null);
      });
    }
  }, [deleteId, onDeleteQuestion, closeWarningModal]);

  const handleDelete = (id: string) => {
    setDeleteId(id);
    openWarningModal();
  };

  const columns = formatBasicQuestionColumns(
    basicQuestions,
    handleEdit,
    (id: string) => handleDelete(id),
    isRowLocked,
    isReadOnly,
    track,
  );

  const displayQuestions = useMemo(() => {
    if (isReadOnly) {
      return basicQuestions.filter((q) => q.isIncluded);
    }
    return basicQuestions;
  }, [basicQuestions, isReadOnly]);

  const table = useReactTable({
    columns,
    data: displayQuestions,
    getCoreRowModel: getCoreRowModel(),
    // Tanstack will use indices by default. Use the question UUID instead to
    // keep a stable reference during drag and drop
    getRowId: ({ id }) => id,
    state: {
      columnVisibility: getColumnVisibilityState(isReadOnly),
    },
  });

  const isDraggable = (question: ReviewPageBasicQuestion) => {
    return !(isReadOnly || isRowLocked(question) || !question.questionType);
  };

  return (
    <TableWrapper
      addQuestionTestId={
        addQuestionTestId ??
        buildTestId("builder-review", "basic-questions", "add-question")
      }
      addQuestionTooltipDisabled
      closeWarningModal={closeWarningModal}
      confirmDelete={confirmDelete}
      emptyState={emptyState}
      hasRows={table.getRowModel().rows.length > 0}
      header="Basic questions"
      isReadOnly={isReadOnly}
      isUnframed={isUnframed}
      onAddQuestionClick={() => {
        track("ADD QUESTION + Button Clicked");
        openQuestionModal();
      }}
      tableHeaders={table.getFlatHeaders()}
      infoTooltipLabel="Simple questions that are asked verbatim without any follow-up questions"
      warningModalOpened={warningModalOpened}
    >
      <DragDropProvider onDragEnd={onDragEnd}>
        {table.getRowModel().rows.map((row, index) => (
          <SortableTableRow
            cellTDClassName="px-4 min-h-14 py-2"
            className={(row, isDragging) => [
              "bg-white",
              "border-gray-200",
              "border-t",
              "min-h-14",
              "p-0",
              isDragging && [
                "drop-shadow-[0_0_12px_rgba(0,0,0,0.10)]",
                "border-b",
              ],
            ]}
            disabled={!isDraggable(row.original)}
            id={row.original.id}
            index={index}
            key={row.id}
            renderCell={(cell) =>
              flexRender(cell.column.columnDef.cell, cell.getContext())
            }
            row={row}
          />
        ))}
      </DragDropProvider>
    </TableWrapper>
  );
};
